import { useCallback, useEffect, useRef, useState } from 'react';
import {
  listProfileTracks,
  listUserMediaPhotos,
} from '../services/mediaService';

export default function useProfileMediaLibrary(userId, { enabled = true, audience = 'viewer' } = {}) {
  const [profileTracks, setProfileTracks] = useState([]);
  const [userMediaPhotos, setUserMediaPhotos] = useState([]);
  const tracksRequestRef = useRef(0);
  const photosRequestRef = useRef(0);
  const tracksUserIdRef = useRef(null);
  const photosUserIdRef = useRef(null);

  const refreshProfileTracks = useCallback(async () => {
    const requestId = tracksRequestRef.current + 1;
    tracksRequestRef.current = requestId;
    if (!enabled || !userId) {
      tracksUserIdRef.current = null;
      setProfileTracks([]);
      return [];
    }
    const normalizedUserId = String(userId);
    if (tracksUserIdRef.current !== normalizedUserId) {
      tracksUserIdRef.current = normalizedUserId;
      setProfileTracks([]);
    }
    try {
      const tracks = audience === 'public'
        ? await listProfileTracks(userId, { audience: 'public' })
        : await listProfileTracks(userId);
      const normalized = Array.isArray(tracks) ? tracks : [];
      if (tracksRequestRef.current !== requestId) {
        return [];
      }
      setProfileTracks(normalized);
      return normalized;
    } catch (error) {
      if (tracksRequestRef.current !== requestId) {
        return [];
      }
      console.error('Failed to load profile tracks:', error);
      setProfileTracks([]);
      return [];
    }
  }, [audience, enabled, userId]);

  const refreshUserMediaPhotos = useCallback(async () => {
    const requestId = photosRequestRef.current + 1;
    photosRequestRef.current = requestId;
    if (!enabled || !userId) {
      photosUserIdRef.current = null;
      setUserMediaPhotos([]);
      return [];
    }
    const normalizedUserId = String(userId);
    if (photosUserIdRef.current !== normalizedUserId) {
      photosUserIdRef.current = normalizedUserId;
      setUserMediaPhotos([]);
    }
    try {
      const photos = audience === 'public'
        ? await listUserMediaPhotos(userId, { audience: 'public' })
        : await listUserMediaPhotos(userId);
      const normalized = Array.isArray(photos) ? photos : [];
      if (photosRequestRef.current !== requestId) {
        return [];
      }
      setUserMediaPhotos(normalized);
      return normalized;
    } catch (error) {
      if (photosRequestRef.current !== requestId) {
        return [];
      }
      console.error('Failed to load media photos:', error);
      setUserMediaPhotos([]);
      return [];
    }
  }, [audience, enabled, userId]);

  useEffect(() => {
    refreshProfileTracks();
    return () => {
      tracksRequestRef.current += 1;
    };
  }, [refreshProfileTracks]);

  useEffect(() => {
    refreshUserMediaPhotos();
    return () => {
      photosRequestRef.current += 1;
    };
  }, [refreshUserMediaPhotos]);

  return {
    profileTracks,
    setProfileTracks,
    refreshProfileTracks,
    userMediaPhotos,
    setUserMediaPhotos,
    refreshUserMediaPhotos,
  };
}
